/**
 * A fingerprint of a background image that does not depend on its colours.
 *
 * Designers usually make one picture and then colour it for each product: the
 * images have the same size and the same composition, and only the colours
 * are different. The Defaults panel copies the position of a background to
 * the same picture of the other products (DefaultScenePanel/replicateBg.ts).
 * It finds that picture with this fingerprint, because the list position of
 * the picture is different in each product.
 *
 * The fingerprint is the image made grey and small (FINGERPRINT_SIZE squared),
 * and the edges of that grey image. Two images are compared by the normalized
 * cross-correlation of both: a change of hue, brightness or contrast does not
 * change it. A recoloured copy of a picture gives about 0.98, a different
 * picture of the same scene about 0.5 (measured on the 2026.3 Releases
 * backgrounds of IntelliJ IDEA, PyCharm and WebStorm).
 */

/** The side of the grey image, in pixels. */
export const FINGERPRINT_SIZE = 32;

/** The side of the image that is drawn first, then averaged down. */
const SAMPLE_SIZE = FINGERPRINT_SIZE * 4;

export interface BgFingerprint {
  /** The grey image, FINGERPRINT_SIZE squared, row by row. */
  luma: Float32Array;
  /** The edge strength of the grey image, (FINGERPRINT_SIZE - 1) squared. */
  edges: Float32Array;
  /** Width divided by height of the image. */
  aspect: number;
}

/**
 * The fingerprint of `size` × `size` RGBA pixels (a canvas `ImageData`).
 * `aspect` is the aspect of the original image: the pixels are stretched to
 * a square.
 */
export function fingerprintFromRgba(
  rgba: ArrayLike<number>,
  size: number,
  aspect: number
): BgFingerprint {
  if (rgba.length !== size * size * 4) {
    throw new Error(`Expected ${size * size * 4} RGBA values, got ${rgba.length}`);
  }
  if (size % FINGERPRINT_SIZE !== 0) {
    throw new Error(`The sample size ${size} is not a multiple of ${FINGERPRINT_SIZE}`);
  }
  const block = size / FINGERPRINT_SIZE;
  const luma = new Float32Array(FINGERPRINT_SIZE * FINGERPRINT_SIZE);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      // A transparent pixel shows the base colour: it counts as black.
      const alpha = rgba[i + 3]! / 255;
      const grey = (0.299 * rgba[i]! + 0.587 * rgba[i + 1]! + 0.114 * rgba[i + 2]!) * alpha;
      luma[Math.floor(y / block) * FINGERPRINT_SIZE + Math.floor(x / block)]! += grey;
    }
  }
  for (let i = 0; i < luma.length; i++) luma[i]! /= block * block;
  return {luma, edges: edgesOf(luma, FINGERPRINT_SIZE), aspect};
}

function edgesOf(luma: Float32Array, size: number): Float32Array {
  const side = size - 1;
  const edges = new Float32Array(side * side);
  for (let y = 0; y < side; y++) {
    for (let x = 0; x < side; x++) {
      const here = luma[y * size + x]!;
      const dx = luma[y * size + x + 1]! - here;
      const dy = luma[(y + 1) * size + x]! - here;
      edges[y * side + x] = Math.sqrt(dx * dx + dy * dy);
    }
  }
  return edges;
}

/** Normalized cross-correlation, from -1 to 1. A flat image gives 0. */
function correlation(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) throw new Error("Fingerprints of different sizes");
  let meanA = 0;
  let meanB = 0;
  for (let i = 0; i < a.length; i++) {
    meanA += a[i]!;
    meanB += b[i]!;
  }
  meanA /= a.length;
  meanB /= b.length;
  let sum = 0;
  let sumA = 0;
  let sumB = 0;
  for (let i = 0; i < a.length; i++) {
    const da = a[i]! - meanA;
    const db = b[i]! - meanB;
    sum += da * db;
    sumA += da * da;
    sumB += db * db;
  }
  const norm = Math.sqrt(sumA * sumB);
  return norm > 0 ? sum / norm : 0;
}

/**
 * How alike the compositions of two images are, from -1 to 1: the mean of
 * the correlation of the grey images and of their edges.
 */
export function fingerprintSimilarity(a: BgFingerprint, b: BgFingerprint): number {
  return (correlation(a.luma, b.luma) + correlation(a.edges, b.edges)) / 2;
}

const cache = new Map<string, Promise<BgFingerprint>>();

/**
 * The fingerprint of an image URL, in the browser. The background CDN allows
 * cross-origin reads, so the pixels can be read from a canvas. Use the small
 * WebP thumbnail of a background: the composition is the same, the download
 * is small. One URL is loaded once per page.
 */
export function loadBgFingerprint(url: string): Promise<BgFingerprint> {
  const cached = cache.get(url);
  if (cached) return cached;
  const promise = new Promise<BgFingerprint>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.decoding = "async";
    image.onload = () => {
      try {
        if (!image.naturalWidth || !image.naturalHeight) {
          throw new Error(`The image has no size: ${url}`);
        }
        const canvas = document.createElement("canvas");
        canvas.width = SAMPLE_SIZE;
        canvas.height = SAMPLE_SIZE;
        const context = canvas.getContext("2d", {willReadFrequently: true});
        if (!context) throw new Error("No 2D canvas context");
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = "high";
        context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        const {data} = context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        resolve(fingerprintFromRgba(data, SAMPLE_SIZE, image.naturalWidth / image.naturalHeight));
      } catch (error) {
        reject(error);
      }
    };
    image.onerror = () => reject(new Error(`Could not load the image: ${url}`));
    image.src = url;
  });
  // A failed load can be tried again later.
  promise.catch(() => cache.delete(url));
  cache.set(url, promise);
  return promise;
}
